import { unreadCount } from '../../shared/functions/chat/chatUtils.js'
import {
  checkActiveMember,
  isIntranetRoute
} from '../../shared/functions/appHeader.js'

// Local page-title map for all /tickets/* (and other) routes that
// IntranetAppHeader must label correctly. Mirrors the TITLES map in
// editor/packages/shared/components/AppHeader.js — kept here so the
// intranet bundle is self-sufficient regardless of how Parcel resolves
// the shared AppHeader at build time.
const INTRANET_TITLES = {
  '/calendar': 'Calendar',
  '/chat': 'Chat',
  '/tickets': 'My view',
  '/tickets/my': 'My view',
  '/tickets/general': 'General',
  '/tickets/feed': 'Feed',
  '/tickets/canvas': 'Canvas',
  '/tickets/stories': 'Stories',
  '/tickets/epics': 'Epics',
  '/tickets/board': 'Board',
  '/tickets/standups': 'Standups',
  '/tickets/requests': 'Requests',
  '/tickets/requests/inbound': 'Inbound',
  '/tickets/requests/outbound': 'Outbound',
  '/tickets/requests/audit': 'Audit / Reports',
  '/tickets/resolutions': 'Resolutions',
  '/meet': 'Meet',
  '/system': 'System',
  '/dashboard': 'Dashboard'
}

const INTRANET_PREFIX_TITLES = [
  ['/tickets/requests', 'Requests'],
  ['/tickets/resolutions', 'Resolutions'],
  ['/tickets', 'Tickets'],
  ['/meet/', 'Call'],
  ['/documents/', 'Documents'],
  ['/documents', 'Documents'],
  ['/workspaces', 'Workspaces'],
  ['/admin', 'Admin']
]

const intranetPageTitle = (path) => {
  if (!path) return ''
  if (INTRANET_TITLES[path]) return INTRANET_TITLES[path]
  if (path.startsWith('/meet/') && path.endsWith('/transcript'))
    return 'Transcript'
  for (const [prefix, label] of INTRANET_PREFIX_TITLES) {
    if (path.startsWith(prefix)) return label
  }
  return 'Canvas'
}

const NAV_LINKS = [
  { label: 'Main', href: '/' },
  { label: 'People', href: '/people' },
  { label: 'My', href: '/my' },
  { label: 'Announcements', href: '/announcements' },
  { label: 'Company', href: '/company' }
]

const totalUnread = (root) => {
  if (!root) return 0
  const me = root.currentUserEmail
  const members = root.channelMembers || []
  let total = 0
  for (const mm of members) {
    if (mm.member_email !== me) continue
    const msgs = root.messagesByChannel?.[mm.channel_id] || []
    total += unreadCount(msgs, mm.last_read_at, me)
  }
  return total
}

export const IntranetAppHeader = {
  extends: 'AppHeader',

  onRender: (el, s) => {
    if (!el._notifInit && s.root?.currentUserEmail) {
      el._notifInit = true
      el.call('requestBrowserPermission')
      el.call('subscribeNotifications')
      el.call('fetchNotifications')
    }
  },

  // Recompute chat unread only when the underlying data changed — otherwise
  // every unrelated root update would loop through all memberships.
  onUpdate: (el, s) => {
    const root = s.root
    const members = root.channelMembers
    const msgs = root.messagesByChannel
    if (el._lastMembers === members && el._lastMsgs === msgs) return
    el._lastMembers = members
    el._lastMsgs = msgs
    const next = totalUnread(root)
    const cur = root.navUnreadCounts?.chat || 0
    if (next !== cur) {
      root.update({
        navUnreadCounts: { ...(root.navUnreadCounts || {}), chat: next }
      })
    }
  },

  // Override the shared AppHeader PageTitle so the intranet bundle resolves
  // the correct sub-page label from its own TITLES map rather than the
  // shared one (which may not reflect the latest keys when Parcel caches
  // an older build of AppHeader.js). All /tickets/* routes that were
  // previously falling through to the PREFIX_TITLES catch-all are handled
  // by INTRANET_TITLES above.
  PageTitle: {
    text: (el, s) => intranetPageTitle(s.root?.currentPath || s.root?.route || '')
  },

  Links: {
    hide: (el, s) => !checkActiveMember(el) || !isIntranetRoute(s),
    theme: 'document',
    background: 'overlayStrong',
    round: 'D',
    flow: 'x',
    gap: 'X',
    padding: 'X',
    align: 'center center',
    margin: '- auto',
    childExtends: 'AppNavLink',
    childrenAs: 'state',
    children: () => NAV_LINKS
  },

  SearchBar: {
    hide: (el, s) => !checkActiveMember(el) || isIntranetRoute(s),
    Form: {
      Input: {
        placeholder: 'Search across everything…',
        onInput: (e, el) => el.call('globalSearch', e.target.value),
        onKeydown: (e, el) => {
          if (e.key === 'Escape') {
            el.call('closeNavSearch')
            el.node.blur()
          }
        }
      }
    },
    Dropdown: {
      ResultGroups: {
        flow: 'y',
        if: (el, s) => !!s.root?._navSearchGrouped?.length,
        children: (el, s) => s.root?._navSearchGrouped || [],
        childExtends: 'NavSearchResultGroup',
        childrenAs: 'state'
      }
    }
  },

  // Shared AppHeader already lays out the icon/button slots
  // (AppWorkspaceSwitcher, CalOnlineUsersBar, Messenger, NotificationWrapper,
  // AppAssistantToggle, MeWrapper) — intranet just gates the whole rail on
  // membership and customises the avatar menu.
  UserActions: {
    hide: (el) => !checkActiveMember(el),

    MeWrapper: {
      MeMenu: {
        AdminItem: {
          order: -1,
          extends: 'Link',
          text: 'Admin',
          color: 'title',
          href: '/admin',
          hide: (el) => !el.call('currentUserIsAdmin')
        },
        SignOutItem: {
          // migrated 2026-04-27 — flat sdk.logout() handles token clearing,
          // emits SIGNED_OUT to subscribers, and triggers the SDK's own
          // bridge teardown (federated logoutAll lives in functions/auth.js).
          // After logout, redirect to /login so the user lands on a fresh
          // sign-in page rather than on the now-tokenless intranet (which
          // would only flash empty data before its own auth gate kicks in).
          href: '/login',
          onClick: async (e, el) => {
            e.preventDefault()
            try { await el.sdk?.logout?.() } catch {}
            el.router('/login', el.getRoot())
          }
        }
      }
    }
  }
}
